import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { AlertTriangle, Home, RefreshCw, ArrowLeft, ServerCrash, Search, Lock } from 'lucide-react';

// Generic Error Page Component
export const ErrorPage = ({ 
  code = '500', 
  title = 'Something went wrong', 
  message = 'We encountered an unexpected error. Please try again.',
  showDashboard = true,
  showRefresh = true,
  showBack = true
}) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {/* Error Icon */}
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-red-100 mb-4">
            <AlertTriangle className="w-12 h-12 text-red-500" />
          </div>
          <div className="text-8xl font-outfit font-bold text-slate-200">{code}</div>
        </div>

        {/* Error Message */}
        <h1 className="text-2xl font-outfit font-bold text-slate-900 mb-3">{title}</h1>
        <p className="text-slate-600 font-jakarta mb-8">{message}</p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {showBack && (
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="border-slate-300 hover:bg-slate-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          )}
          {showRefresh && (
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
              className="border-slate-300 hover:bg-slate-100"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          )}
          {showDashboard && (
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-primary hover:bg-primary-hover"
            >
              <Home className="w-4 h-4 mr-2" />
              Go to Dashboard
            </Button>
          )}
        </div>

        {/* Footer */}
        <p className="mt-8 text-sm text-slate-400">
          If this problem persists, please contact support.
        </p>
      </div>
    </div>
  );
};

// 404 Not Found
export const NotFoundPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-blue-100 mb-4">
            <Search className="w-12 h-12 text-blue-500" />
          </div>
          <div className="text-8xl font-outfit font-bold text-slate-200">404</div>
        </div>

        <h1 className="text-2xl font-outfit font-bold text-slate-900 mb-3">Page Not Found</h1>
        <p className="text-slate-600 font-jakarta mb-8">
          The page you&apos;re looking for doesn&apos;t exist or has been moved.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="border-slate-300 hover:bg-slate-100"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-primary hover:bg-primary-hover"
          >
            <Home className="w-4 h-4 mr-2" />
            Go to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

// 500 Server Error
export const ServerErrorPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-orange-100 mb-4">
            <ServerCrash className="w-12 h-12 text-orange-500" />
          </div>
          <div className="text-8xl font-outfit font-bold text-slate-200">500</div>
        </div>

        <h1 className="text-2xl font-outfit font-bold text-slate-900 mb-3">Server Error</h1>
        <p className="text-slate-600 font-jakarta mb-8">
          Our servers are experiencing issues. We&apos;re working to fix this as quickly as possible.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
            className="border-slate-300 hover:bg-slate-100"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
          <Button
            onClick={() => window.location.href = '/dashboard'}
            className="bg-primary hover:bg-primary-hover"
          >
            <Home className="w-4 h-4 mr-2" />
            Go to Dashboard
          </Button>
        </div>

        <p className="mt-8 text-sm text-slate-400">
          If this problem persists, please contact support.
        </p>
      </div>
    </div>
  );
};

// 403 Forbidden / Unauthorized
export const UnauthorizedPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-yellow-100 mb-4">
            <Lock className="w-12 h-12 text-yellow-600" />
          </div>
          <div className="text-8xl font-outfit font-bold text-slate-200">403</div>
        </div>

        <h1 className="text-2xl font-outfit font-bold text-slate-900 mb-3">Access Denied</h1>
        <p className="text-slate-600 font-jakarta mb-8">
          You don&apos;t have permission to access this page. Please contact an administrator if you believe this is an error.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="border-slate-300 hover:bg-slate-100"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-primary hover:bg-primary-hover"
          >
            <Home className="w-4 h-4 mr-2" />
            Go to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

// Campaign Archived/Unavailable Page (for public landing pages)
export const CampaignArchivedPage = ({ campaignName }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="max-w-lg w-full text-center">
        {/* Decorative Elements */}
        <div className="mb-8 relative">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-64 h-64 bg-primary/10 rounded-full blur-3xl"></div>
          </div>
          <div className="relative">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-slate-800 border border-slate-700 mb-6">
              <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
            </div>
          </div>
        </div>

        <h1 className="text-3xl font-outfit font-bold text-white mb-4">
          This Event Has Ended
        </h1>
        
        {campaignName && (
          <p className="text-xl text-slate-400 mb-4">{campaignName}</p>
        )}
        
        <p className="text-slate-500 font-jakarta mb-8 max-w-sm mx-auto">
          This event page is no longer available. The event has concluded and registrations are closed.
        </p>

        <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700 inline-block">
          <p className="text-sm text-slate-400">
            Looking for something else? Visit our main website for upcoming events.
          </p>
        </div>

        {/* Footer */}
        <p className="mt-12 text-xs text-slate-600">
          Powered by Kaizen Events
        </p>
      </div>
    </div>
  );
};

// Registration Closed Page (for when registration is closed or disabled)
export const RegistrationClosedPage = ({ campaignName, endDate, customMessage }) => {
  return (
    <div className="text-center py-8">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 mb-4">
        <Lock className="w-10 h-10 text-red-500" />
      </div>
      <h3 className="text-2xl font-outfit font-bold text-slate-900 mb-3">Registration Closed</h3>
      {customMessage ? (
        // Super-admin-authored, replaces the default copy entirely — see
        // CampaignDetail.js's RegistrationStatusToggle. Plain text only (no HTML), so a
        // newline-respecting wrap is enough; nothing here is sanitized as markup.
        <p className="text-slate-600 mb-4 max-w-sm mx-auto whitespace-pre-line">
          {customMessage}
        </p>
      ) : (
        <>
          <p className="text-slate-600 mb-4 max-w-sm mx-auto">
            Registration for <span className="font-semibold">{campaignName || 'this event'}</span> is currently not available.
          </p>
          {endDate ? (
            <p className="text-sm text-slate-500">
              Registration period ended on {new Date(endDate).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          ) : (
            <p className="text-sm text-slate-500">
              Registration has been closed by the organizer.
            </p>
          )}
        </>
      )}
    </div>
  );
};

// Registration Coming Soon (countdown)
export const RegistrationComingSoon = ({ campaignName, startDate }) => {
  const [timeLeft, setTimeLeft] = React.useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  React.useEffect(() => {
    if (!startDate) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = new Date(startDate).getTime();
      const difference = target - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [startDate]);

  return (
    <div className="text-center py-12">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
        <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h3 className="text-xl font-outfit font-bold text-slate-900 mb-2">Registration Opens Soon</h3>
      <p className="text-slate-600 mb-6">
        Registration for {campaignName || 'this event'} will open on{' '}
        {new Date(startDate).toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        })}
      </p>

      {/* Countdown */}
      <div className="flex justify-center gap-4">
        {[
          { value: timeLeft.days, label: 'Days' },
          { value: timeLeft.hours, label: 'Hours' },
          { value: timeLeft.minutes, label: 'Minutes' },
          { value: timeLeft.seconds, label: 'Seconds' }
        ].map((item, idx) => (
          <div key={idx} className="text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-1">
              <span className="text-2xl font-outfit font-bold text-primary">
                {String(item.value).padStart(2, '0')}
              </span>
            </div>
            <span className="text-xs text-slate-500">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ErrorPage;
